import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { Button } from 'yoso';
import { BaseCard } from '~/components/Card';
import {
  Leaderboard,
  useChallengeLeaderboard,
} from '~/components/Challenge/Leaderboard/ChallengeLeaderboard.hooks';
import { useProceedScoreUp } from '~/components/Challenge/Score/ChallengeScore.hooks';
import { T } from '~/components/I18n';
import { Chevron } from '~/components/Icon/v2/Chevron';
import { format } from '~/components/Util/number';
import styles from './ChallengeTipBanner.module.scss';

export type ChallengeTipBannerProps = {
  className?: string;
} & Leaderboard;

export const ChallengeTipBanner = ({
  className,
  week,
  myScore = 0,
  myRank = 0,
  rivalRank = 0,
  rivalScore = 0,
  rankers,
}: ChallengeTipBannerProps) => {
  const { isFetchedAfterMount } = useChallengeLeaderboard(
    {
      week,
    },
    { enabled: !!week }
  );
  const { mutateAsync, isLoading } = useProceedScoreUp();
  const onlyMe = rankers.length === 1 && myRank === 1;
  const handleButtonClick = useCallback(() => {
    if (!isLoading) mutateAsync();
  }, [isLoading, mutateAsync]);
  const diffScore = rivalScore - myScore;

  const body = useMemo(() => {
    if (onlyMe) {
      return (
        <>
          <strong>
            <T name="citizen_challenge_ranking_scoreup_desc3_1" />
          </strong>{' '}
          <span>
            <T name="citizen_challenge_ranking_scoreup_desc3_2" />
          </span>
        </>
      );
    } else if (myRank === 1) {
      return (
        <>
          <strong>
            <T name="citizen_challenge_ranking_scoreup_desc2_1" />
          </strong>{' '}
          <span>
            <T name="citizen_challenge_ranking_scoreup_desc2_2" />
          </span>
        </>
      );
    } else {
      return (
        <>
          <strong>
            <T
              name="citizen_challenge_ranking_scoreup_desc1_1"
              options={{ 0: rivalRank || '-', count: rivalRank, ordinal: true }}
            />
          </strong>{' '}
          <span>
            <T
              name="citizen_challenge_ranking_scoreup_desc1_2"
              options={[format(diffScore, { zeroAsEmpty: !isFetchedAfterMount })]}
            />
          </span>
        </>
      );
    }
  }, [onlyMe, myRank, rivalRank, diffScore, isFetchedAfterMount]);

  return (
    <BaseCard color="muted" className={classNames(styles.root, className)}>
      <div className={styles.body}>{body}</div>
      <div className={styles.aside}>
        <Button
          className={styles.button}
          color="secondary"
          size="small"
          onClick={handleButtonClick}
          data-event="click"
          data-event-target="round_scoreup_btn_in_leaderboard">
          <T name="citizen_challenge_scoreup_name" />
          <Chevron className={styles.icon} />
        </Button>
      </div>
    </BaseCard>
  );
};
